import { Node as PMNode, NodeType } from 'prosemirror-model';
import { insertPoint } from 'prosemirror-transform';

const LIST_TYPES = ['bullet_list', 'ordered_list'];

/**
 * Finds where a block element (figure, table, box) goes and inserts it so that
 * it never ends up inside a list item.
 *
 * - Not in a list: same as before, `insertPoint` decides.
 * - In a list item the element goes right after the TOP-LEVEL item holding the
 *   cursor: the outermost list is split there (the second half keeps counting
 *   for ordered lists). An EMPTY item at any depth (Enter at the end of an item)
 *   is removed first, together with any list or item it would leave empty.
 * - The search for the list stops at the first node that is neither a list nor a list
 *   item (box, table cell, blockquote), and where the element cannot leave the list
 *   (e.g. a table inside a table cell) the plain insert point is used as before.
 *
 * Returns the transaction and the position of the inserted node, or null if the
 * node does not fit anywhere. The caller dispatches the transaction.
 */
export function insertBlockOutsideList(state: any, nodeType: NodeType, node: PMNode): { tr: any, at: number } | null {
  const doc = state.doc;
  const point = insertPoint(doc, state.selection.from, nodeType);
  if (point == null) { return null; }

  const plain = () => {
    const tr = state.tr;
    tr.insert(point, node);
    return { tr, at: point };
  };

  // the chain of lists and list items directly around the insert point; it stops at
  // the first other node (a box, a table cell, a blockquote ...) so we never leave those
  const $p = doc.resolve(point);
  let d = $p.depth;
  while (d >= 1 && ($p.node(d).type.name === 'list_item' || LIST_TYPES.includes($p.node(d).type.name))) { d--; }
  const listDepth = d + 1;
  const itemDepth = $p.depth;
  if (listDepth > itemDepth || $p.node(itemDepth).type.name !== 'list_item' || !LIST_TYPES.includes($p.node(listDepth).type.name)) {
    return plain();
  }

  const tr = state.tr;
  const list = $p.node(listDepth);
  const listStart = $p.before(listDepth);
  const listEnd = $p.after(listDepth);
  const index = $p.index(listDepth);
  const topEnd = $p.after(listDepth + 1);
  const container = $p.node(listDepth - 1);
  const listIndex = $p.index(listDepth - 1);

  // which node to remove: the empty item, or its list / parent item when that would be left empty
  const item = $p.node(itemDepth);
  const itemIsEmpty = item.childCount === 1 && item.firstChild!.isTextblock && item.firstChild!.content.size === 0;
  let rmDepth = 0;
  if (itemIsEmpty) {
    let r = itemDepth;
    for (;;) {
      if (r === listDepth + 1 || $p.node(r - 1).childCount > 1) { rmDepth = r; break; }
      if ($p.node(r - 2).childCount > 1) { rmDepth = r - 1; break; }
      r -= 2;
    }
  }
  const topItemRemoved = rmDepth === listDepth + 1;

  if (topItemRemoved && list.childCount === 1) {
    if (!container.canReplaceWith(listIndex, listIndex + 1, nodeType)) { return plain(); }
    tr.replaceWith(listStart, listEnd, node);
    return { tr, at: listStart };
  }

  const before = index + (topItemRemoved ? 0 : 1);
  const after = list.childCount - index - 1;
  const slot = before === 0 ? listIndex : listIndex + 1;
  // cannot leave the list here (e.g. a table cell does not take a table): keep the old behaviour
  if (!container.canReplaceWith(slot, slot, nodeType)) { return plain(); }

  if (rmDepth) { tr.delete($p.before(rmDepth), $p.after(rmDepth)); }

  if (before === 0) {
    tr.insert(listStart, node);
    return { tr, at: listStart };
  }
  if (after === 0) {
    const pos = tr.mapping.map(listEnd);
    tr.insert(pos, node);
    return { tr, at: pos };
  }

  // split the top-level list after the item the cursor was in; keep numbering going
  const splitAt = tr.mapping.map(topEnd);
  const attrsAfter = { ...list.attrs };
  if (list.type.spec.attrs && 'order' in list.type.spec.attrs) {
    attrsAfter.order = (list.attrs.order ?? 1) + before;
  }
  tr.split(splitAt, 1, [{ type: list.type, attrs: attrsAfter }]);
  tr.insert(splitAt + 1, node);
  return { tr, at: splitAt + 1 };
}
